import { Stack, Text } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { price as priceUtil } from '@eti/utils';
import { cancelForAnyReason, flexibleTicket, ProductModalHeader } from '@eti/products';
import { css, etiColors, getTextStyles, mediaQueries } from '@eti/styles';
import sumBy from 'lodash/sumBy';
import { ScreenReaderPrice } from '@eti/components';
import { useCheapestPaymentMethod } from '../../../../../../../utils/paymentMethods';
import PriceBreakdownTravelerGroup from '../../../PriceBreakdownTravelerGroup';
import PriceBreakdownPreSelectedProducts from '../../../PriceBreakdownPreSelectedProducts';
import type { Ticket, TicketPriceModalInfoProps } from '../../model';
import CartPriceSummary from '../../../../../../../widgets/cart/components/CartPriceSummary';
import CartToggleableRow from '../../../../../../../widgets/cart/components/CartToggleableRow';
import {
  CartRow,
  CartSubSectionTitle,
  Price,
} from '../../../../../../../widgets/cart/components/CartComponents';
import {
  getTotalPriceForFlexibleTicket,
  getTripTotalPrice,
} from '../../../../../utils/resultUtils';
import { ModalDialog } from './ModalDialog';
import { getPaymentMethods } from './helper';

const additionalInformationStyles = css`
  font-size: 0.875rem;
  ${getTextStyles()};

  @media ${mediaQueries.large.up} {
    font-size: 0.875rem;
  }

  & i {
    color: ${etiColors.grayScale[10]};
    display: block;
    font-size: 0.75rem;
    line-height: 1.3;
  }
`;

const paymentMethodsTitleStyle = css`
  margin-bottom: 12px;
`;

interface TicketPriceInfoModalProps {
  handleClose: () => void;
  isOpen: boolean;
  isRyanAirTicketingOrMarketingCarrierPresent?: boolean;
  isVI?: boolean;
  ticket: Ticket;
  ticketPriceModalInfo: TicketPriceModalInfoProps;
}

export const TicketPriceInfoModal = ({
  handleClose,
  isOpen,
  isRyanAirTicketingOrMarketingCarrierPresent,
  isVI,
  ticket,
  ticketPriceModalInfo,
}: TicketPriceInfoModalProps) => {
  const { t } = useTranslation();
  const { cfarTicketPrices, flexibleTicketPrices } = ticket;
  const {
    discountAmount = 0,
    marketingCarrierNames,
    paymentMethodPrices = [],
    preSelectedExtraProducts = [],
    travelerPrices,
    travelerPriceBreakdown = [],
  } = ticketPriceModalInfo;
  const subTotalPrice = getTripTotalPrice(
    travelerPriceBreakdown,
    flexibleTicketPrices,
    cfarTicketPrices,
  );
  const paymentMethods = getPaymentMethods(paymentMethodPrices);
  const selectedPaymentMethod = useCheapestPaymentMethod(paymentMethods);
  const preSelectedProductsTotalPrice = preSelectedExtraProducts?.length
    ? sumBy(preSelectedExtraProducts, 'totalPrice')
    : 0;
  const totalPrice = getTripTotalPrice(travelerPrices, flexibleTicketPrices, cfarTicketPrices);
  return (
    <ModalDialog
      handleClose={handleClose}
      id="ticket-price-info"
      isOpen={isOpen}
      title={t('TripDetails.AirSupplierCostSpecification.Header')}
    >
      <Stack role="document" spacing={16}>
        {flexibleTicketPrices && (
          <>
            <ProductModalHeader
              productName={flexibleTicket.name}
              productTitle={t('flexibleTicketHeaderTitle')}
            />
            <Text
              asHtml
              className={additionalInformationStyles}
              data-testid="ticket-price-info-flexible-ticket-description"
            >
              {t('flexibleTicketDescription')}
            </Text>
          </>
        )}
        {cfarTicketPrices && (
          <>
            <ProductModalHeader
              productName={cancelForAnyReason.name}
              productTitle={t('cancelForAnyReasonTitle')}
            />
            <Text
              asHtml
              className={additionalInformationStyles}
              data-testid="ticket-price-info-cfar-ticket-description"
            >
              {t('cancelForAnyReasonTicketDescription')}
            </Text>
          </>
        )}
        {travelerPriceBreakdown?.map(
          ({ ageType, markup, price, totalPriceSum, quantity, vat }: any) => (
            <PriceBreakdownTravelerGroup
              ageType={ageType}
              isRyanAirTicketingOrMarketingCarrierPresent={
                isRyanAirTicketingOrMarketingCarrierPresent
              }
              isVI={isVI}
              key={ageType}
              marketingCarrierNames={marketingCarrierNames}
              markup={markup}
              price={price}
              quantity={quantity}
              totalPrice={totalPriceSum}
              vat={vat}
            />
          ),
        )}
        {preSelectedExtraProducts?.length > 0 && (
          <PriceBreakdownPreSelectedProducts preSelectedExtraProducts={preSelectedExtraProducts} />
        )}
        {flexibleTicketPrices && (
          <div data-testid="trip-priceBreakdown-extraServices-flexible-ticket">
            <CartRow>
              <CartSubSectionTitle data-testid="trip-priceBreakdown-extraServices-label">
                {t('Cart.ExtraServices.Label')}
              </CartSubSectionTitle>
            </CartRow>
            <CartRow>
              <span data-testid="trip-priceBreakdown-flex-label">
                {t('Product.FlexibleTicket.PriceSpecification.Total')}
              </span>
              <Price aria-hidden data-testid="trip-priceBreakdown-flex-price">
                {priceUtil.formatPrice(getTotalPriceForFlexibleTicket(flexibleTicketPrices))}
              </Price>
              <ScreenReaderPrice
                price={priceUtil.formatPrice(getTotalPriceForFlexibleTicket(flexibleTicketPrices))}
              />
            </CartRow>
          </div>
        )}
        {cfarTicketPrices && (
          <div data-testid="trip-priceBreakdown-extraServices-cfar-ticket">
            <CartRow>
              <CartSubSectionTitle data-testid="trip-priceBreakdown-extraServices-label">
                {t('Cart.ExtraServices.Label')}
              </CartSubSectionTitle>
            </CartRow>
            <CartRow>
              <span data-testid="trip-priceBreakdown-cfar-label">
                {t('Product.CancelForAnyReasonTicket.PriceSpecification.Total')}
              </span>
              <Price aria-hidden data-testid="trip-priceBreakdown-cfar-price">
                {priceUtil.formatPrice(cfarTicketPrices)}
              </Price>
              <ScreenReaderPrice price={priceUtil.formatPrice(cfarTicketPrices)} />
            </CartRow>
          </div>
        )}
        <CartPriceSummary
          dataTestId="trip-priceBreakdown-summary"
          discountAmount={discountAmount}
          // @ts-ignore - WEBTECH-1361
          paymentMethods={paymentMethods}
          selectedPaymentMethod={selectedPaymentMethod}
          shouldShowPaymentOptions={false}
          subTotalPrice={subTotalPrice + preSelectedProductsTotalPrice}
          totalPrice={totalPrice + preSelectedProductsTotalPrice - discountAmount}
        />
        <CartToggleableRow
          data={paymentMethods}
          dataTestId="trip-priceBreakdown-paymentMethods"
          title={t('Cart.PaymentFees.Label')}
          titleStyle={paymentMethodsTitleStyle}
        />
      </Stack>
    </ModalDialog>
  );
};
